import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'

import QrScanner from 'qr-scanner'

import {
  describeCameraError,
  describeCameraSupport,
  QrCameraError
} from './model/camera-error'
import { createScanGate, ScanGateMode, ScanOffer } from './model/scan-gate'

export type QrScannerStatus =
  | 'idle' // ainda não tentou abrir a câmera
  | 'starting' // aguardando permissão / stream
  | 'scanning' // câmera aberta, lendo
  | 'processing' // consumidor tratando um código lido
  | 'error' // câmera indisponível

export type QrScanHandlerResult = void | boolean | 'accepted' | 'rejected'

export type UseQrScannerOptions = {
  /**
   * Recebe o código lido. Devolver `false` ou `'rejected'` (inclusive via
   * promise) libera aquele código para ser lido de novo — é assim que o
   * desafio de nível rearma a leitura quando o backend recusa a resposta.
   */
  onSuccess: (code: string) => QrScanHandlerResult | Promise<QrScanHandlerResult>
  /** Suspende a leitura sem derrubar a câmera. Alternar o valor rearma o portão. */
  disabled?: boolean
  /** `single`: uma leitura por acionamento. `continuous`: várias, com dedupe. */
  mode?: ScanGateMode
  cooldownMs?: number
  preferredCamera?: QrScanner.FacingMode
  /** Fração do menor lado do vídeo usada como região de leitura. */
  scanRegionRatio?: number
  onError?: (error: QrCameraError | null) => void
}

export type UseQrScannerResult = {
  videoRef: RefObject<HTMLVideoElement>
  status: QrScannerStatus
  error: QrCameraError | null
  retry: () => void
}

const DEFAULT_SCAN_REGION_RATIO = 0.8
const DOWN_SCALED_SIZE = 400

const wasAccepted = (result: QrScanHandlerResult): boolean =>
  !(result === false || result === 'rejected')

export const useQrScanner = ({
  onSuccess,
  disabled = false,
  mode = 'single',
  cooldownMs = 0,
  preferredCamera = 'environment',
  scanRegionRatio = DEFAULT_SCAN_REGION_RATIO,
  onError
}: UseQrScannerOptions): UseQrScannerResult => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerRef = useRef<QrScanner | null>(null)

  const [status, setStatus] = useState<QrScannerStatus>('idle')
  const [error, setError] = useState<QrCameraError | null>(null)
  const [attempt, setAttempt] = useState(0)

  // O portão vive fora do ciclo de render: recriá-lo perderia o dedupe.
  const gate = useMemo(
    () => createScanGate({ mode, cooldownMs }),
    [mode, cooldownMs]
  )

  // Callbacks em ref para o efeito de setup não depender delas — recriar o
  // scanner a cada render era o que deixava a câmera pendurada.
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  const disabledRef = useRef(disabled)

  useEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => {
    disabledRef.current = disabled
  }, [disabled])

  const reportError = useCallback((next: QrCameraError | null) => {
    setError(next)
    onErrorRef.current?.(next)
  }, [])

  const handleDecode = useCallback(
    async (raw: string) => {
      if (disabledRef.current) return

      const offer: ScanOffer = gate.offer(raw)
      if (!offer.accepted) return

      setStatus('processing')

      try {
        const result = await onSuccessRef.current(offer.code)
        gate.settle(offer.code, wasAccepted(result) ? 'accepted' : 'rejected')
      } catch (err) {
        // Falha do consumidor não pode travar o leitor: libera para nova tentativa.
        gate.settle(offer.code, 'rejected')
        console.error('[QrCodeReader] falha ao tratar o código lido', err)
      } finally {
        setStatus(current => (current === 'processing' ? 'scanning' : current))
      }
    },
    [gate]
  )

  const handleDecodeRef = useRef(handleDecode)

  useEffect(() => {
    handleDecodeRef.current = handleDecode
  }, [handleDecode])

  // Alternar `disabled` rearma o portão — preserva o comportamento histórico
  // do leitor da bottom nav, em que sair de `loading` liberava nova leitura.
  useEffect(() => {
    gate.reset()
  }, [disabled, gate])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return undefined

    const unsupported = describeCameraSupport(
      typeof navigator === 'undefined' ? undefined : navigator,
      typeof window === 'undefined' ? undefined : window.isSecureContext
    )

    if (unsupported) {
      setStatus('error')
      reportError(unsupported)
      return undefined
    }

    const scanner = new QrScanner(
      video,
      result => {
        void handleDecodeRef.current(result?.data)
      },
      {
        // O decodificador dispara "não achei QR Code" a cada frame: ruído puro.
        onDecodeError: () => undefined,
        preferredCamera,
        highlightScanRegion: true,
        highlightCodeOutline: true,
        // O overlay fica a cargo do qr-scanner: ele insere e remove o nó no
        // `destroy`. Um nó nosso seria arrancado do DOM sem o React saber, e
        // o `retry` reinstalaria o scanner sobre um elemento já destacado.
        calculateScanRegion: element => {
          const size =
            Math.min(element.videoWidth, element.videoHeight) * scanRegionRatio

          return {
            x: (element.videoWidth - size) / 2,
            y: (element.videoHeight - size) / 2,
            width: size,
            height: size,
            downScaledWidth: DOWN_SCALED_SIZE,
            downScaledHeight: DOWN_SCALED_SIZE
          }
        }
      }
    )

    scannerRef.current = scanner
    setStatus('starting')
    reportError(null)

    let cancelled = false

    scanner
      .start()
      .then(() => {
        if (cancelled) return
        setStatus('scanning')
      })
      .catch(err => {
        if (cancelled) return
        setStatus('error')
        reportError(describeCameraError(err))
      })

    return () => {
      cancelled = true
      // `destroy` para o stream e solta o srcObject do vídeo — `stop` sozinho
      // deixava o indicador de câmera do aparelho aceso depois de sair da tela.
      scanner.destroy()
      scannerRef.current = null
      gate.reset()
    }
  }, [attempt, gate, preferredCamera, reportError, scanRegionRatio])

  const retry = useCallback(() => {
    gate.reset()
    setAttempt(current => current + 1)
  }, [gate])

  return { videoRef, status, error, retry }
}
